import { LStorage } from '@shared/Storage';
import axios, { AxiosResponse } from 'axios';
import { Ajax2 } from '@shared/Ajax';
import { Notify } from '@shared/ui-native/Notify';
import { WebviewInterface } from './WebviewInterface';
import { jwtDecode } from 'jwt-decode';

export interface IAuthToken {
    accessToken: string;
    idToken: string;
    refreshToken: string;
    clientId?: string;
    expiresIn: number;
    createdAt?: number; // set by service here
}

function getJwtIssuedAt(jwt: string): number | null {
    if (!jwt) {
        return null;
    }

    const parts = jwt.split('.');
    if (parts.length < 2) {
        return null;
    }

    try {
        const decodedPayload = jwtDecode<{ iat?: number }>(jwt);
        const issuedAt = decodedPayload?.iat;

        return typeof issuedAt === 'number' ? issuedAt : null;
    } catch {
        return null;
    }
}

// responses:
// 401 {"errorMessage":"User not authorized"} // wrong pwd
// 401 {"errorMessage":"Token expired. The Token has expired on ___."}
function fetchToken(username: string, password: string): Promise<any> {
    clearToken();

    return axios({
        // not using Ajax wrapper because we should not intercept these calls
        url: kuura.getIamAddress() + '/auth/token',
        method: 'POST',
        timeout: 10000,
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
        },
        data: {
            grant_type: 'password',
            username,
            password,
        },
    })
        .then(resp => resp?.data)
        .then(token => {
            if (token.accessToken) {
                const iat = getJwtIssuedAt(token.accessToken);
                token.createdAt = iat ? iat * 1000 : Date.now();
                token.expiresIn = 3600;
                storeToken(token);
            }
        })
        .catch(error => {
            if (error?.response?.status === 401) {
                LStorage.setItem('iamNotEnabled', 'true');
            }
        });
}

function forceLogout() {
    clearToken();
    const message = `${kuura.getMessage('js_main.session_was_invalidated')} ${kuura.getMessage('js_main.relog')}`;
    Notify.warn(message, 4000);
    submitLogoutForm();
}

function submitLogoutForm(): void {
    const formElements: HTMLCollectionOf<HTMLFormElement> = document.forms;

    if (formElements.length === 0) return;

    const logoutForm: HTMLFormElement | undefined = (formElements as any).logout ?? (formElements as any).logoutForm;

    if (logoutForm) {
        logoutForm.submit();
        return;
    }

    return;
}

async function refreshToken(): Promise<IAuthToken> {
    const token: IAuthToken | null = getStoredToken();

    /* A call for token refresh does not return a new creation timestamp so we
       create pre-emptively a timestamp for the refreshed token */
    const newCreatedAt = Date.now();

    if (!token) {
        throw { errorMsg: 'No token' };
    }

    if (!token.refreshToken) {
        throw { errorMsg: 'No refresh token' };
    }

    const resp: AxiosResponse = await axios({
        url: kuura.getIamAddress() + '/auth/token',
        method: 'POST',
        timeout: 10000,
        params: {
            grant_type: 'refresh_token',
            refresh_token: token.refreshToken,
            client_id: token.clientId,
        },
    });

    const refreshedPayload = resp?.data;
    if (!refreshedPayload?.accessToken) {
        throw { errorMsg: 'Failed to refresh token' };
    }

    const iat = getJwtIssuedAt(refreshedPayload.accessToken);

    const newToken: IAuthToken = {
        ...token,
        ...refreshedPayload,
        createdAt: iat ? iat * 1000 : newCreatedAt,
    };

    // Cognito uses one refresh token per session, so we need to keep the same refresh token stored after refreshing
    storeToken(newToken);

    return newToken;
}

function getStoredToken(): IAuthToken | null {
    try {
        return JSON.parse(LStorage.getItem(Auth.STORAGE_KEY)) || null;
    } catch {}

    return null;
}

function clearToken() {
    LStorage.removeItem(Auth.STORAGE_KEY);
    LStorage.removeItem('iamNotEnabled');
}

function storeToken(token: IAuthToken) {
    LStorage.setItem(Auth.STORAGE_KEY, JSON.stringify(token));
}

function isTokenExpired(token: IAuthToken): boolean {
    return Date.now() >= token.createdAt + token.expiresIn * 1000;
}


async function fetchTokenFromServer(): Promise<IAuthToken | null> {
    const tokenPath = '/oauth2/id-token';

    const response = await Ajax2({
        url: '/kuura' + tokenPath,
        method: 'GET',
    });

    if (response.status === false) {
        return null;
    }

    if (response.token) {
        const iat = getJwtIssuedAt(response.token);

        const token: IAuthToken = {
            accessToken: response.access_token,
            idToken: response.token,
            refreshToken: response.refresh_token ?? '',
            clientId: response.client_id,
            createdAt: iat ? iat * 1000 : Date.now(),
            expiresIn: 3600,
        };

        storeToken(token);
        return token;
    }

    return null;
}

/**
 * Acquires a valid token through SSO fetch or token refresh.
 * Only called once per token cycle — all concurrent callers share the same promise.
 */
async function acquireToken(): Promise<IAuthToken> {
    // 1. Try fetching fresh token from server (SSO)
    try {
        const ssoToken = await fetchTokenFromServer();
        if (ssoToken && !isTokenExpired(ssoToken)) {
            return ssoToken;
        }
    } catch (error) {
        console.error('Failed to fetch token from server:', error);
    }

    // 2. Try refreshing the existing stored token
    const storedToken = getStoredToken();
    if (storedToken?.refreshToken) {
        try {
            return await refreshToken();
        } catch {}
    }

    // 3. All recovery methods exhausted
    if (!WebviewInterface.hasMobileInterface()) {
        forceLogout();
    }
    throw new Error('Unable to obtain valid auth token');
}

// Single shared promise for all concurrent token requests
let pendingTokenPromise: Promise<IAuthToken> | null = null;

async function getToken(): Promise<IAuthToken> {
    // Fast path: valid cached token (synchronous — no race condition possible)
    const cached = getStoredToken();
    if (cached && !isTokenExpired(cached)) {
        return cached;
    }

    // Slow path: deduplicate all concurrent callers behind one promise
    if (!pendingTokenPromise) {
        pendingTokenPromise = acquireToken().finally(() => {
            pendingTokenPromise = null;
        });
    }

    return pendingTokenPromise;
}

let userPrivilegesCache = null;
async function hasIamPrivilege(privilege: string): Promise<boolean> {
    if (Array.isArray(userPrivilegesCache)) {
        console.log('returning from cache');
        return Promise.resolve(userPrivilegesCache.includes(privilege));
    }

    let privileges = [];
    const req: any = {
        url: kuura.getIamAddress() + `/authorization/user-privileges`,
        method: 'get',
        useToken: true,
        contentType: 'urlencoded',
    };
    const globalPromise = Ajax2({
        // fetch global user privileges
        ...req,
        data: {
            userUuid: kuura.getUserUuid(),
        },
    });
    const orgPromise = Ajax2({
        // fetch user privileges on current project
        ...req,
        data: {
            userUuid: kuura.getUserUuid(),
            organizationUuid: kuura.getProjectOrganization().uuid,
        },
    });
    try {
        const results = await Promise.allSettled([globalPromise, orgPromise]);
        results.forEach(result => {
            if (Array.isArray((result as any).value)) {
                privileges.push(...(result as any).value);
            }
        });
    } catch {}
    userPrivilegesCache = privileges.map(p => p.privilegeName);
    return userPrivilegesCache.includes(privilege);
}

export const Auth = {
    STORAGE_KEY: 'authToken',
    clearToken,
    fetchToken,
    refreshToken,
    getStoredToken,
    getToken,
    isTokenExpired,
    hasIamPrivilege,
};
